// Register.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useLocale from '../hooks/useLocale'; 
import axios from 'axios'; 
import './register.css';
import InfoTooltip from '../components/InfoTooltip'; // Adjust path



function Register() {
    const { handleLanguageChange, t } = useLocale();
    const navigate = useNavigate(); // Hook for redirecting after successful registration

  // --- State Variables ---
  const [username, setUsername] = useState(''); // Corresponds to 'uname'
  const [email, setEmail] = useState('');     // Corresponds to 'email'
  const [password, setPassword] = useState(''); // Corresponds to 'pwrd'
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState(''); // For success/error messages
  const [isLoading, setIsLoading] = useState(false); // To disable button during submit

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default page reload *first*
    setIsLoading(true);
    setMessage('');

    // --- 1. Trigger Browser's Native Validation UI ---
    // Check if the form passes all HTML5 validation rules (required, type="email", etc.)
    if (!e.target.checkValidity()) { // e.target is the form element
      // If not valid, tell the browser to display its native error messages (the "box thing")
      e.target.reportValidity();
      setIsLoading(false); // Re-enable button
      return; // Stop the rest of the function
    }

    // --- We removed the custom JS check for empty fields: ---
    // if (!username || !email || !password || !confirmPassword) { ... }
    // The e.target.checkValidity() call above now handles the `required` attribute check natively.

    // --- 2. Perform Custom JavaScript Validations (that HTML5 can't handle) ---

    // Check if Passwords Match (Still needed)
    if (password !== confirmPassword) {
      // Use *our* message display for custom errors
      setMessage(t('API_AUTH_PASSWORD_MISMATCH', 'Passwords do not match.'));
      setIsLoading(false);
      return;
    }

    // Check Minimum Password Length (Still needed, though minLength attribute could also be used)
    const minPasswordLength = 8;
    if (password.length < minPasswordLength) {
       // Use *our* message display for custom errors
      setMessage(t('API_AUTH_PASSWORD_TOO_SHORT', 'Password must be at least 8 characters long.'));
      setIsLoading(false);
      return;
    }

    // --- If ALL validations pass (native and custom) ---
    console.log('All validations passed!');
    try {
        const response = await axios.post(
          '/api/register', // Assuming endpoint based on login URL
          {
            // Send data matching backend expectations (based on your table)
            uname: username,
            email: email,
            pwrd: password
          }
        );
  
        // --- Handle SUCCESS ---
        setMessage(response.data.message || t('API_AUTH_REGISTRATION_SUCCESS_VERIFY_EMAIL', 'Registration successful! Please check your email for a verification link.')); // Use message from backend if provided
        // Clear the form fields on success
        setUsername('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
  
        // Optional: Redirect to login page after a short delay
        setTimeout(() => {
          navigate('/login'); // Redirect using the navigate hook
        }, 2000); // 2-second delay
  
      } catch (error) {
        console.error("Registration API call failed:", error.response || error.message);
    
        // 1. Extract the specific code from the backend response
        const errorCode = error.response?.data?.errorCode;
    
        // 2. Use the specific code with t(), or a generic fallback key if no code exists
        const messageKey = errorCode || 'API_SERVER_ERROR_GENERIC'; // Use generic for unexpected errors
    
        // 3. Set the message using the determined key
        setMessage(
            t(messageKey, // Use the key (e.g., API_AUTH_EMAIL_TAKEN or API_SERVER_ERROR_GENERIC)
              'Registration failed. Please try again later.' // Final fallback text if key missing
             )
        );
        // Remember setIsLoading(false); belongs in the finally block
    } finally {
        // --- Always run after try or catch ---
        setIsLoading(false); // Re-enable button
      }
    }; // End handleSubmit
  // --- Render Component ---
  return (
    <div>
      <button onClick={() => handleLanguageChange('en')}>English</button> 
      <button onClick={() => handleLanguageChange('es')}>Spanish</button>

      
      <h2>{t('auth_register_title', 'Register')}</h2>

      <form onSubmit={handleSubmit} className="register-form"> {/* Add class to form */}

      {/* Username Field */}
      <div className="form-group">
        <label htmlFor="reg-username">{t('auth_register_username_label', 'Username')}:</label>
        
        <input
          type="text"
          id="reg-username"
          value={username} 
          onChange={(e) => setUsername(e.target.value)} 
          required
          disabled={isLoading}
        />
      </div>

      {/* Email Field */}
      <div className="form-group">
        <label htmlFor="reg-email">{t('auth_register_email_label', 'Email')}:</label>
        <input
          type="email"
          id="reg-email"
          value={email} 
          onChange={(e) => setEmail(e.target.value)} 
          required
          disabled={isLoading}
        />
      </div>

      {/* Password Field */}
      <div className="form-group">



        <label htmlFor="reg-password">
          
        <InfoTooltip
                         text={t('auth_register_password_hint', 'Password must be 8 characters long.')}
                         idSuffix="upload-types"
                         place="top"
                     />

          {t('auth_register_password_label', 'Password')}:

        

        </label>
        <input
          type="password"
          id="reg-password"
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          required
          disabled={isLoading}
        />
      </div>                 

      {/* Confirm Password Field */}
      <div className="form-group">
        <label htmlFor="reg-confirm-password">{t('auth_register_confirm_password_label', 'Confirm Password')}:</label>
        
        <input
          type="password"
          id="reg-confirm-password"
          value={confirmPassword} 
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <button type="submit" disabled={isLoading}>
          {isLoading ? (t('auth_register_loading_button', 'Registering...')) : (t('auth_register_submit_button', 'Register'))}
          
        </button>
      </form>

      {/* Display Messages */}
      {message && <p style={{ color: message.includes('successful') || message.includes('proceed') ? 'green' : 'red', marginTop: '1rem', textAlign: 'center' }}>{message}</p>}
      {/* Link back to Login */}
      <p style={{ marginTop: '1rem' }}>
        {t('auth_register_already_account_prompt', 'Already have an account?')}{' '}
        <Link to="/login" className="app-link">
          {t('auth_register_login_link', 'Log in here')}
        </Link>
        
        
      </p>
    </div>
  );
}

export default Register;